
import Vue, { PropType } from "vue";
import { debounce } from "lodash";
import { mapActions } from "vuex";
import NoticeEntity from "~/entities/Notice";


class Selector {
  public id!: string;
  public label!: string;
  public coverage?: number;
  public cost?: number;
  public afterText?: string;
  public manualEntry?: string;
  public constructor(init?: Partial<Selector>) {
    Object.assign(this, init);
  }
}

export default Vue.extend({
  name: "LargeSelect",
  props: {
    options: {
      type: Array as PropType<Selector[]>,
      default: () => [],
    },
    title: { type: String, default: "" },
    // Native hover hint (HTML title attribute) on the select button. Separate from `title`
    // (the visible floating label) and `arialabel` (screen-reader name). Bill (BIK-2106):
    // add descriptive hover/alt text to the form's dropdown arrows, matching the Ship1
    // From/To icons. Only rendered when a parent passes it, so other LargeSelect uses are
    // unaffected.
    hint: { type: String, default: "" },
    name: { type: String, default: "" },
    errors: { type: Boolean, default: false },
    selection: { type: [Boolean, Object], default: false },
    arialabel: { type: String, default: "" },
    customH: { type: String, default: "" },
    allowEmpty: { type: Boolean, default: false },
    diffKey: { type: String, default: "" },
    disabled: {
      type: Boolean,
      default: false,
    },
    useLabelCombination: {
      type: Boolean,
      default: false,
    },
    useId: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false
    },
    disableButtonClasses: { type: Boolean, default: false },
  },
  data() {
    return {
      isOpen: false,
      selected: {} as Selector,
      show: false,
      protectionOption: {} as Selector,
      manualValue: "",
      manualFee: 0,
      manualFeeLabel: "",
      manualAfterText: "",
      manualValidationError: false,
      savedScrollPosition: 0,
      debouncedUpdateProtection: () => {},
      tooltip_PremiumProtection: null as NoticeEntity | null,
    };
  },
  watch: {
    selection(newVal) {
      if (newVal) {
        this.select(newVal);
      } else if (newVal === false) {
        // Clear selection when prop is explicitly false
        this.selected = {} as Selector;
        this.show = false;
      }
    },
    isOpen(newValue) {
      if (newValue) {
        this.$nextTick(() => {
          const tray = this.$refs.tray;
          if (!tray || Array.isArray(tray) || !('getBoundingClientRect' in tray)) {
            return;
          }
          const trayRect = tray.getBoundingClientRect();
          const bottomOffset = window.innerHeight - trayRect.bottom;
          if (bottomOffset < 0) {
            window.scrollBy({
              top: -bottomOffset + 20,
              behavior: 'smooth'
            });
          }
        });
      } else {
        this.restoreScrollPosition();
      }
    }
  },
  async mounted() {
    if (this.options) {
      if (this.allowEmpty) {
        this.show = true;
        this.selected = { id: "", label: "-" };
      } else {
        this.selected = this.options[0];
      }
    }
    if (this.selection) {
      this.select(this.selection);
    }
    this.debouncedUpdateProtection = debounce(this.updateProtection, 300);
    if (this.name === "premiumProtection") {
      await this.preloadPremiumProtectionTooltipNotice();
    }
  },
  methods: {
    ...mapActions({
      getProtectionOption: "ship/getProtectionOption",
      getAlert: "errorsBag/getAlert",
    }),
    async preloadPremiumProtectionTooltipNotice(): Promise<void> {
      this.tooltip_PremiumProtection = await this.getAlert({ name: "ToolTip-PremiumProtection" });
    },
    onShow() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.saveScrollPosition();
      } else {
        this.restoreScrollPosition();
      }
    },
    saveScrollPosition() {
      // Save the current scroll position when the tray is opened
      this.savedScrollPosition = window.scrollY;
    },

    restoreScrollPosition() {
      // Scroll back to the saved position smoothly
      if (this.savedScrollPosition !== undefined) {
        window.scrollTo({
          top: this.savedScrollPosition,
          behavior: 'smooth'
        });
      }
    },
    close() {
      this.isOpen = false;
    },
    select(item: Selector) {
      this.selected = item;
      this.show = true;
      this.isOpen = false;
      this.$emit("selectedItem", item);
      if (this.name || item) {
        this.$emit("selectionMade", {
          option: item,
          name: this.name ? this.name : this.title,
        });
      }
    },
    async validateManualInput() {
      this.manualValidationError = false;
      const value = parseFloat(this.manualValue);
      if (value && value > 0) {
        if (value <= 100) {
          this.manualValue = "";
          this.manualFee = 0;
          this.manualFeeLabel = "";
          this.manualAfterText = "";
          this.select(this.options[0]);
          return;
        }
        if (value > 20000) { // if greater than 20000, select 20000
          const option20K = await (this as any).getProtectionOption(20000);
          if (option20K) {
            this.protectionOption = option20K
          }
        }
        const preselected = this.options.find((x) => x.coverage === value);
        if (preselected) {
          this.manualValue = "";
          this.manualFee = 0;
          this.manualFeeLabel = "";
          this.manualAfterText = "";
          this.select(preselected);
          return;
        }

        const selection = new Selector({
            label: this.protectionOption.label,
            coverage: this.protectionOption.coverage,
            cost:  this.protectionOption.cost,
            afterText: this.protectionOption.afterText,
          });
        this.select(selection);
        this.isOpen = false;
      } else {
        this.manualValue = "";
      }
    },
    async updateProtection () {
      this.manualValidationError = false;
      const value = parseFloat(this.manualValue);
      if (value && value >= 100) {
        if (value > 20000) { // check over max error case
          this.manualValidationError = true;
          return;
        }

        // check if the value entered matches an existing preset
        const preselected = this.options.find((x) => x.coverage === value);
        if (preselected) {
          this.protectionOption = preselected;
          this.manualFee = preselected.cost ?? 0;
          this.manualFeeLabel = `(+${this.manualFee.toFixed(2)})`;
          this.manualAfterText = preselected.afterText ?? "";
        }

        // figure custom coverage amount
        const option = await (this as any).getProtectionOption(value);
        if (option) {
          this.protectionOption = option;
          this.manualFee = option.cost;
          this.manualFeeLabel = `(+${this.manualFee.toFixed(2)})`;
          this.manualAfterText = option.afterText;
        }
      } else {
        this.manualFee = 0;
        this.manualFeeLabel = "";
        this.manualAfterText = "";
      }
    }, // Waits 300ms after the last keypress
  }
});
